import { Component, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { NameType } from 'src/app/shared/models/models';
import { StandardNamnService } from '../standard-namn.service';
import { finalize } from 'rxjs';

@Component({
    selector: 'app-standard-namn-edit-dialog',
    templateUrl: './standard-namn-edit-dialog.component.html',
    standalone: false
})
export class StandardNamnEditDialogComponent {
  private standardNamnService = inject(StandardNamnService);
  protected config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);

  protected error = false;
  protected newName = '';
  protected loading = false;
  protected errorMessage = '';

  private readonly MAX_LENGTH = 100;

  protected get oldName(): string {
    return this.config.data.standardNamnObject.standardNamn;
  }

  protected get isSameAsOld(): boolean {
    return this.newName === this.oldName;
  }

  protected get isTooLong(): boolean {
    return this.newName.length > this.MAX_LENGTH;
  }

  /**
   * Exactly one capital letter followed by lowercase letters only
   */
  protected get hasValidCapitalization(): boolean {
    return /^[A-ZÅÄÖ][a-zåäö-]+(( |\/)[a-zåäö-]*)*$/.test(this.newName);
  }

  protected get isAllCaps(): boolean {
    return this.newName === this.newName.toUpperCase();
  }

  protected get validationError(): string | null {
    if (!this.newName) {
      return 'Namn krävs.';
    }

    if (this.isTooLong) {
      return `Max ${this.MAX_LENGTH} tecken tillåtna.`;
    }

    if (this.isSameAsOld) {
      return 'Det nya namnet måste skilja sig från det gamla namnet.';
    }

    if (this.isAllCaps) {
      return 'Namnet får inte bestå av enbart versaler.';
    }

    if (!this.hasValidCapitalization) {
      return 'Namnet måste börja med en stor bokstav följt av små bokstäver.';
    }

    return null;
  }

  protected get isValid(): boolean {
    return this.validationError === null;
  }

  protected save() {
    if (!this.isValid) {
      return;
    }
    this.error = false;
    this.loading = true;
    const newObject = { ...this.config.data.standardNamnObject, standardNamn: this.newName };

    this.updateStandardNamn(newObject);
  }

  protected close() {
    this.ref.close();
  }

  private updateStandardNamn(row: { type: NameType; id: string; standardNamn: string }) {
    this.standardNamnService
      .updateStandardnamn(row.type, row.id, row.standardNamn)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => (console.log('Standardnamn uppdaterat'), this.ref.close(this.config.data.standardNamnObject)),
        error: (e) => {
          this.error = true;
          if (e.status === 400) {
            this.errorMessage = 'Kunde ej spara. Standardnamnet du försöker ändra till finns redan.';
          } else {
            this.errorMessage = 'Kunde ej spara. Okänt fel har inträffat.';
          }
        },
      });
  }
}
